import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RootLayout } from "@/components/layout/RootLayout";
import { useAuth } from "@/context/AuthContext";
import {
  Gift,
  Users,
  TrendingUp,
  CheckCircle,
  Share2,
  ArrowRight,
  Sparkles,
  Target,
  Clock,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { PostLoginAuthLink } from "@/components/routing/PostLoginAuthLink";

/**
 * Referrals.
 */
export default function Referrals() {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <RootLayout>
      <div className="bg-gradient-to-br from-orange-50 via-white to-orange-100">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 rounded-full mb-6 shadow-lg">
            <Gift className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">Referral Program</h1>

          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Share TellerWand with friends and earn free Pro subscription months
          </p>

          {/* Reward Cards - Clean Design */}
          <div className="flex justify-center items-center gap-4 md:gap-8 mb-8 flex-wrap">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-100">
              <div className="text-4xl font-bold text-orange-600 mb-1">+2</div>
              <div className="text-sm text-gray-600">Months for You</div>
            </div>
            <div className="text-3xl text-orange-300 font-light">+</div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-100">
              <div className="text-4xl font-bold text-orange-600 mb-1">+1</div>
              <div className="text-sm text-gray-600">For Friend</div>
            </div>
            <div className="text-3xl text-orange-300 font-light">+</div>
            <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-100">
              <div className="text-4xl font-bold text-orange-600 mb-1">+1-3</div>
              <div className="text-sm text-gray-600">On Upgrade</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {user ? (
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                onClick={() => navigate("/referrals/dashboard")}
              >
                <Gift className="w-5 h-5 mr-2" />
                View My Dashboard
              </Button>
            ) : (
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                asChild
              >
                <PostLoginAuthLink mode="signup">
                  <Gift className="w-5 h-5 mr-2" />
                  Get Started Free
                </PostLoginAuthLink>
              </Button>
            )}
            <Button size="lg" variant="outline" onClick={() => navigate("/faq")}>
              Learn More
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">How It Works</h2>

            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Share2 className="w-8 h-8 text-orange-600" />
                </div>
                <div className="bg-orange-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-3">
                  1
                </div>
                <h3 className="font-bold mb-2 text-lg">Share Your Link</h3>
                <p className="text-gray-600 text-sm">
                  Get your unique referral link and share via WhatsApp, email, or social media
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-orange-600" />
                </div>
                <div className="bg-orange-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-3">
                  2
                </div>
                <h3 className="font-bold mb-2 text-lg">They Sign Up</h3>
                <p className="text-gray-600 text-sm">
                  Your friend joins and adds their first asset or transaction within 7 days
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-orange-600" />
                </div>
                <div className="bg-orange-600 text-white text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center mx-auto mb-3">
                  3
                </div>
                <h3 className="font-bold mb-2 text-lg">Get Rewarded</h3>
                <p className="text-gray-600 text-sm">
                  You get +2 months Pro, they get +1 month Pro trial. Rewards apply instantly!
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Why Participate */}
        <section className="py-16 bg-gradient-to-br from-orange-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">Why Join?</h2>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="p-6 bg-white shadow-sm">
                  <Target className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="font-bold mb-2">Earn Up to 6 Months Free</h3>
                  <p className="text-sm text-gray-600">
                    Refer just 3 friends to earn 6 months of free Pro subscription per year. That's
                    ₹594 in value!
                  </p>
                </Card>

                <Card className="p-6 bg-white shadow-sm">
                  <Sparkles className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="font-bold mb-2">Instant Rewards</h3>
                  <p className="text-sm text-gray-600">
                    No waiting! Rewards are applied immediately when your friend qualifies.
                  </p>
                </Card>

                <Card className="p-6 bg-white shadow-sm">
                  <TrendingUp className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="font-bold mb-2">Bonus on Upgrades</h3>
                  <p className="text-sm text-gray-600">
                    Get +1 month for monthly or +3 months for annual upgrades. Up to 5 months per
                    friend!
                  </p>
                </Card>

                <Card className="p-6 bg-white shadow-sm">
                  <Users className="w-8 h-8 text-orange-600 mb-3" />
                  <h3 className="font-bold mb-2">Help Friends Succeed</h3>
                  <p className="text-sm text-gray-600">
                    Your friends get 1 month Pro trial to experience premium features. Win-win!
                  </p>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Qualification */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <Clock className="w-12 h-12 text-orange-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Simple Qualification</h2>
              <p className="text-gray-600 mb-8">
                For rewards to be issued, your friend must complete these within 7 days:
              </p>

              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Verify Email</div>
                    <div className="text-sm text-gray-600">Click verification link we send</div>
                  </div>
                </div>

                <div className="flex items-start gap-3 p-4 bg-orange-50 rounded-lg border border-orange-100">
                  <CheckCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold mb-1">Add Activity</div>
                    <div className="text-sm text-gray-600">Add 1 asset OR 1 transaction</div>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 mt-6">
                Deadline: 7 days from signup • Attribution window: 10 days from link click
              </p>
            </div>
          </div>
        </section>

        {/* CTA Section - TellerWand Orange */}
        <section className="py-16 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-white">
              Ready to Start Earning?
            </h2>
            <p className="text-lg md:text-xl mb-8 text-white/90 max-w-2xl mx-auto">
              Join TellerWand today and start sharing with friends to earn up to 6 months of free
              Pro subscription!
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              {user ? (
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-orange-600 hover:bg-orange-50"
                  onClick={() => navigate("/referrals/dashboard")}
                >
                  <Gift className="w-5 h-5 mr-2" />
                  View My Dashboard
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="secondary"
                  className="bg-white text-orange-600 hover:bg-orange-50"
                  asChild
                >
                  <PostLoginAuthLink mode="signup">
                    <Gift className="w-5 h-5 mr-2" />
                    Get Started Free
                  </PostLoginAuthLink>
                </Button>
              )}
              <Button
                size="lg"
                variant="secondary"
                className="bg-white text-orange-600 hover:bg-orange-50"
                onClick={() => navigate("/faq")}
              >
                Learn More
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className="text-sm mt-6 text-white/75">
              {user ? (
                <>
                  Questions?{" "}
                  <Link to="/contact-us" className="underline font-semibold text-white">
                    Contact Support
                  </Link>
                </>
              ) : (
                <>
                  Already a member?{" "}
                  <PostLoginAuthLink className="underline font-semibold text-white">
                    Sign in
                  </PostLoginAuthLink>{" "}
                  to get your referral link
                </>
              )}
            </p>
          </div>
        </section>
      </div>
    </RootLayout>
  );
}
